import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import DatePicker from "../../Common/DatePicker";
import { DsButton, DsLabel } from "../../../design-system";
import EmployeeCodeStructure from "./EmployeeCodeStructure.jsx";
import {
  EMPLOYEE_CODE_PATTERN_OPTIONS,
  getEmployeeCodeTodayDate,
  loadEmployeeCodePatternState,
  saveManualEmployeeCodePattern,
} from "./employeeCodePatternUtils";

/**
 * Employee Settings → Employee Code — pattern type + automated structure host.
 * Compact radios (no cards); sticky action footer.
 */
export default function EmployeeCodePatternSettings({
  darkMode = false,
  branches = [],
  departments = [],
  showHistory = false,
  onToggleHistory,
  onPatternTypeChange,
}) {
  const [patternType, setPatternType] = useState("automated");
  const [manualEffectiveDate, setManualEffectiveDate] = useState(getEmployeeCodeTodayDate());
  const [savingManual, setSavingManual] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const state = await loadEmployeeCodePatternState();
      if (cancelled) return;
      setPatternType(state.patternType);
      setManualEffectiveDate(state.manualEffectiveDate);
      onPatternTypeChange?.(state.patternType);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const handlePatternChange = (value) => {
    setPatternType(value);
    onPatternTypeChange?.(value);
  };

  const handleSaveManual = async () => {
    setSavingManual(true);
    try {
      const result = await saveManualEmployeeCodePattern(manualEffectiveDate);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success("Manual pattern saved successfully!");
      window.location.reload();
    } finally {
      setSavingManual(false);
    }
  };

  if (loading) {
    return (
      <div className="ds-caption py-6 text-[hsl(var(--ds-muted-fg))]">Loading employee code settings…</div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-y-contain pb-20">
        {/* Pattern type — compact radios, no cards */}
        <div className="border-b border-[hsl(var(--ds-border))] pb-3">
          <DsLabel className="mb-2">Employee Code Pattern</DsLabel>
          <div className="flex flex-wrap gap-x-6 gap-y-2" role="radiogroup" aria-label="Employee code pattern type">
            {EMPLOYEE_CODE_PATTERN_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-start gap-2"
              >
                <input
                  type="radio"
                  name="employeeCodePatternType"
                  value={opt.value}
                  checked={patternType === opt.value}
                  onChange={() => handlePatternChange(opt.value)}
                  className="mt-1 accent-[hsl(var(--ds-primary))]"
                />
                <span>
                  <span className="ds-body block font-medium text-[hsl(var(--ds-fg))]">
                    {opt.label}
                  </span>
                  <span className="ds-caption block text-[hsl(var(--ds-muted-fg))]">
                    {opt.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {patternType === "manual" ? (
          <div className="max-w-sm space-y-2">
            <DsLabel htmlFor="manual-code-effective-date">
              Effective Date <span className="text-[hsl(var(--ds-danger))]">*</span>
            </DsLabel>
            <DatePicker
              id="manual-code-effective-date"
              value={manualEffectiveDate}
              onChange={(e) => setManualEffectiveDate(e.target.value)}
              min={getEmployeeCodeTodayDate()}
              required
              darkMode={darkMode}
              className="ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            />
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              From this date, codes are entered manually at onboarding. No automated prefix or sequence.
            </p>
          </div>
        ) : (
          <EmployeeCodeStructure
            darkMode={darkMode}
            branches={branches}
            departments={departments}
            embedInPatternSettings
          />
        )}
      </div>

      {/* Sticky footer — flat strip, not a floating card */}
      <div className="sticky bottom-0 z-10 mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-3">
        <DsButton
          id="viewHistoryButton"
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onToggleHistory?.()}
        >
          {showHistory ? "Hide History" : "View History"}
        </DsButton>
        {patternType === "manual" && (
          <DsButton
            type="button"
            variant="primary"
            size="sm"
            loading={savingManual}
            disabled={savingManual}
            onClick={handleSaveManual}
          >
            {savingManual ? "Saving…" : "Save Manual Pattern"}
          </DsButton>
        )}
      </div>
    </div>
  );
}
